/**
 * WorkbookImportDialog - Import multiple forms from a single XLSX workbook.
 *
 * Each tab (sheet) of the uploaded workbook becomes a separate form. The flow:
 * download template → upload workbook → validate → confirm the forms to be
 * created → generate them all via the existing createForm mutation.
 */

import { useAuth } from '@/domains/auth';
import { showErrorToast, showSuccessToast } from '@/shared/notifications/toast-utils';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@/shared/ui';
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Download,
  FileSpreadsheet,
  Upload,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useCreateForm, useFormTypes } from '../../hooks/useFormBuilder';
import type { ImportFormMetadata, SheetConversionResult } from '../../utils/excelImportTypes';
import { downloadFormBuilderTemplate } from '../../utils/excelTemplateGenerator';
import { convertWorkbookToForms } from '../../utils/excelWorkbookConverter';
import { isFormLayout } from './types';

interface WorkbookImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImported?: () => void;
}

const INITIAL_METADATA: ImportFormMetadata = {
  description: '',
  image_url: '',
  form_type_id: null,
  layout: 'Horizontal',
  is_dynamic: false,
};

export default function WorkbookImportDialog({
  open,
  onOpenChange,
  onImported,
}: WorkbookImportDialogProps) {
  const { user } = useAuth();
  const { data: formTypesData } = useFormTypes();
  const createForm = useCreateForm();

  const formTypes = formTypesData?.values ?? [];

  const [file, setFile] = useState<File | null>(null);
  const [metadata, setMetadata] = useState<ImportFormMetadata>(INITIAL_METADATA);
  const [sheets, setSheets] = useState<SheetConversionResult[]>([]);
  const [skippedSheets, setSkippedSheets] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [formTypeError, setFormTypeError] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const validForms = useMemo(() => sheets.filter((s) => s.success && s.data), [sheets]);
  const invalidForms = useMemo(() => sheets.filter((s) => !s.success), [sheets]);
  const totalControls = useMemo(
    () => validForms.reduce((sum, s) => sum + s.controlCount, 0),
    [validForms],
  );

  const reset = () => {
    setFile(null);
    setMetadata(INITIAL_METADATA);
    setSheets([]);
    setSkippedSheets([]);
    setErrors([]);
    setFormTypeError(false);
    setIsProcessing(false);
    setShowConfirm(false);
  };

  const handleClose = (next: boolean) => {
    if (!next) reset();
    onOpenChange(next);
  };

  const handleDownloadTemplate = () => {
    try {
      downloadFormBuilderTemplate();
    } catch {
      showErrorToast('Failed to generate the template file.');
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0] ?? null;
    setFile(selected);
    setSheets([]);
    setSkippedSheets([]);
    setErrors([]);
    setShowConfirm(false);
  };

  const handleValidate = async () => {
    if (!metadata.form_type_id) {
      setFormTypeError(true);
      return;
    }
    if (!file) return;

    setFormTypeError(false);
    setIsProcessing(true);
    setErrors([]);

    try {
      const result = await convertWorkbookToForms(file, metadata, user?.id ?? '');
      setSheets(result.sheets);
      setSkippedSheets(result.skippedSheets);
      setErrors(result.errors);
      if (result.sheets.some((s) => s.success)) {
        setShowConfirm(true);
      }
    } catch (error: any) {
      setErrors([error?.message || 'Failed to process workbook']);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleConfirmGenerate = async () => {
    if (validForms.length === 0) return;

    setIsProcessing(true);
    let created = 0;
    const failed: string[] = [];

    for (const sheet of validForms) {
      try {
        // createForm posts the full definition (sections inline) to /form/add.
        await createForm.mutateAsync({
          ...sheet.data!,
          description: metadata.description || sheet.data!.description,
          form_type_id: metadata.form_type_id,
          layout: metadata.layout,
          is_dynamic: metadata.is_dynamic,
        });
        created++;
      } catch {
        failed.push(sheet.sheetName);
      }
    }

    setIsProcessing(false);

    if (created > 0) {
      if (failed.length > 0) {
        showErrorToast(`${created} form(s) created. Failed: ${failed.join(', ')}.`);
      } else {
        showSuccessToast(`${created} form(s) created successfully.`);
      }
      onImported?.();
      handleClose(false);
    } else {
      showErrorToast('No forms could be created. Please try again.');
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        {!showConfirm ? (
          <>
            <DialogHeader>
              <DialogTitle>Import Forms from Excel</DialogTitle>
              <DialogDescription>
                Each tab (sheet) in the workbook becomes a separate form. Download the template to
                see the required structure.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-4">
              <div className="flex justify-end">
                <Button variant="outline" size="sm" onClick={handleDownloadTemplate}>
                  <Download className="w-4 h-4 mr-1.5" />
                  Download XLSX Template
                </Button>
              </div>

              {/* File input */}
              <div className="space-y-1.5">
                <Label htmlFor="workbook-file">Workbook file</Label>
                <label
                  htmlFor="workbook-file"
                  className="flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-lg p-6 cursor-pointer hover:border-[#E84A2B] transition-colors"
                >
                  <Upload className="w-8 h-8 text-muted-foreground" />
                  {file ? (
                    <span className="text-sm font-medium text-[#E84A2B]">{file.name}</span>
                  ) : (
                    <span className="text-sm text-muted-foreground">
                      Click to browse (.xlsx, .xls)
                    </span>
                  )}
                  <Input
                    id="workbook-file"
                    type="file"
                    accept=".xlsx,.xls"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </label>
              </div>

              <p className="text-xs text-muted-foreground">
                These settings apply to all imported forms:
              </p>

              {/* Form type */}
              <div className="space-y-1.5">
                <Label>
                  Form Type <span className="text-red-500">*</span>
                </Label>
                <Select
                  value={metadata.form_type_id ? String(metadata.form_type_id) : undefined}
                  onValueChange={(value) => {
                    setMetadata((prev) => ({ ...prev, form_type_id: Number(value) }));
                    setFormTypeError(false);
                  }}
                >
                  <SelectTrigger className={formTypeError ? 'border-red-500' : ''}>
                    <SelectValue placeholder="Select form type" />
                  </SelectTrigger>
                  <SelectContent>
                    {formTypes.map((type) => (
                      <SelectItem key={type.id} value={String(type.id)}>
                        {type.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {formTypeError && <p className="text-xs text-red-500">Form type is required.</p>}
              </div>

              {/* Description */}
              <div className="space-y-1.5">
                <Label htmlFor="workbook-description">Description (optional)</Label>
                <Textarea
                  id="workbook-description"
                  value={metadata.description}
                  onChange={(e) =>
                    setMetadata((prev) => ({ ...prev, description: e.target.value }))
                  }
                  placeholder="Applied to every imported form"
                  rows={2}
                />
              </div>

              {/* Layout */}
              <div className="space-y-1.5">
                <Label>Layout</Label>
                <Select
                  value={metadata.layout}
                  onValueChange={(value) => {
                    if (!isFormLayout(value)) return;
                    setMetadata((prev) => ({ ...prev, layout: value }));
                  }}
                  disabled={metadata.is_dynamic}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Horizontal">Horizontal</SelectItem>
                    <SelectItem value="Vertical">Vertical</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Dynamic */}
              <div className="flex items-center gap-2">
                <Checkbox
                  id="workbook-dynamic"
                  checked={metadata.is_dynamic}
                  onCheckedChange={(checked) =>
                    setMetadata((prev) => ({
                      ...prev,
                      is_dynamic: checked === true,
                      layout: checked === true ? 'Horizontal' : prev.layout,
                    }))
                  }
                />
                <Label htmlFor="workbook-dynamic" className="cursor-pointer">
                  Is Dynamic Form
                </Label>
              </div>

              {errors.length > 0 && (
                <div className="rounded-md border border-red-300 bg-red-50 dark:bg-red-950 p-3 space-y-1 dark:border-red-900">
                  {errors.map((err) => (
                    <p key={err} className="text-sm text-red-600 dark:text-red-400">
                      {err}
                    </p>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => handleClose(false)} disabled={isProcessing}>
                Cancel
              </Button>
              <Button
                onClick={handleValidate}
                disabled={!file || isProcessing}
                className="bg-[#E84A2B] hover:bg-[#d43d20] text-white"
              >
                {isProcessing ? 'Validating...' : 'Validate & Preview'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <>
            <DialogHeader>
              <DialogTitle>Confirm Form Creation</DialogTitle>
              <DialogDescription>
                {validForms.length} form(s) will be created ({totalControls} total field(s)). Review
                before confirming.
              </DialogDescription>
            </DialogHeader>

            <div className="space-y-2">
              {validForms.map((sheet) => (
                <div
                  key={sheet.sheetName}
                  className="flex items-center gap-3 rounded-md border-l-2 border-[#E84A2B] bg-muted/40 p-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0" />
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-foreground">{sheet.data?.name}</p>
                    <div className="flex gap-1.5 mt-1">
                      <Badge variant="secondary" className="text-[10px]">
                        {sheet.sectionCount} section(s)
                      </Badge>
                      <Badge variant="secondary" className="text-[10px]">
                        {sheet.controlCount} field(s)
                      </Badge>
                    </div>
                  </div>
                </div>
              ))}

              {invalidForms.length > 0 && (
                <div className="rounded-md border border-amber-300 bg-amber-50 dark:bg-amber-950 p-3 space-y-2 dark:border-amber-900">
                  <p className="flex items-center gap-1.5 text-sm font-medium text-amber-700 dark:text-amber-400">
                    <AlertTriangle className="w-4 h-4" />
                    {invalidForms.length} tab(s) will be skipped:
                  </p>
                  {invalidForms.map((sheet) => (
                    <p key={sheet.sheetName} className="text-xs text-amber-700 dark:text-amber-400">
                      <strong>{sheet.sheetName}</strong>: {sheet.errors.join('; ')}
                    </p>
                  ))}
                </div>
              )}

              {skippedSheets.length > 0 && (
                <p className="text-xs text-muted-foreground">
                  Ignored tabs: {skippedSheets.join(', ')}
                </p>
              )}
            </div>

            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setShowConfirm(false)}
                disabled={isProcessing}
              >
                <ArrowLeft className="w-4 h-4 mr-1.5" />
                Back
              </Button>
              <Button
                onClick={handleConfirmGenerate}
                disabled={isProcessing || validForms.length === 0}
                className="bg-[#E84A2B] hover:bg-[#d43d20] text-white"
              >
                <FileSpreadsheet className="w-4 h-4 mr-1.5" />
                {isProcessing ? 'Generating...' : `Create ${validForms.length} Form(s)`}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
